import styled from '@emotion/styled';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Button, Typography } from '@mui/material';
import { cloneDeep, isEqual } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import {
  CatchmentAreaGroupName,
  DistanceLabel,
  DistanceRegEx,
  FilterContainerStyle,
  FilterContainerWrapperStyle,
  GenderLabel,
  InitFilterEntity,
  LABEL_APPLY,
  LABEL_CLEAR,
  LanguageMultiOptionsGroupName,
  ProvinceLabel,
  TotalCountries,
  TotalDistance,
  TotalGenders,
} from './Filter.mock';
import FilterControls from './FilterControls/FilterControls';

import { FilterEntityInterface, FilterInterface } from './Filter.interface';
import ModifiedAutocomplete from './ModifiedMuiSelect/ModifiedAutocomplete';
import ModifiedSelect from './ModifiedMuiSelect/ModifiedSelect';
import RadioGroupSelection from './ModifiedMuiSelect/RadioGroupSelection';
import useDistance, { distanceLabelHandler } from './useDistance';
import useMultiSelection from './useMultiSelection';
import useSelection from './useSelection';

import { usePrimaryEffectEnhancement } from '../../../../style/global/theme';
import TextFieldInput from './ModifiedMuiSelect/TextFieldInput';
import useTextInput from './useTextInput';

const StyledFilterContainer = styled.div(FilterContainerStyle);
const StyledFilterContainerWrapper = styled.div(FilterContainerWrapperStyle);
const StyledFilterOptions = styled.div`
  display: flex;
  flex-direction: column;
`;

const Filter = ({ initFilter, updateFilter, nameOfSearch }: FilterInterface) => {
  const provincesProps = useMultiSelection(initFilter.provinces, 'provinces');
  const languagesProps = useMultiSelection(initFilter.languages, 'languages');
  const distanceProps = useDistance(useSelection(initFilter.distance), DistanceRegEx);
  const genderProps = useSelection(initFilter.gender);
  const countryProps = useSelection(initFilter.country);
  const catchmentAreaProps = useMultiSelection(initFilter.catchmentArea, 'district');
  const specialtyProps = useMultiSelection(initFilter.specialty, 'practice-codes');
  const nameProps = useTextInput(initFilter.name);
  const cityProps = useSelection(initFilter.city);
  const nearProps = useTextInput(initFilter.near);

  const UpdatedFilter: FilterEntityInterface = useMemo(
    () => ({
      distance: distanceProps.checkedEntity,
      gender: genderProps.checkedEntity,
      country: countryProps.checkedEntity,
      languages: languagesProps.checkedEntity,
      catchmentArea: catchmentAreaProps.checkedEntity,
      provinces: provincesProps.checkedEntity,
      specialty: specialtyProps.checkedEntity,
      city: cityProps.checkedEntity,
      name: nameProps.checkedEntity,
      near: nearProps.checkedEntity,
    }),
    [
      languagesProps.checkedEntity,
      provincesProps.checkedEntity,
      distanceProps.checkedEntity,
      countryProps.checkedEntity,
      catchmentAreaProps.checkedEntity,
      genderProps.checkedEntity,
      specialtyProps.checkedEntity,
      cityProps.checkedEntity,
      nameProps.checkedEntity,
      nearProps.checkedEntity,
    ]
  );

  const EnhancedButtonStyle = usePrimaryEffectEnhancement({
    alignSelf: 'flex-end',
    margin: '0.25rem 0',
    height: '1.5rem',
  });

  const applyIsAble = useMemo(() => !isEqual(UpdatedFilter, initFilter), [UpdatedFilter, initFilter]);
  const clearIsAble = useMemo(() => !isEqual(UpdatedFilter, InitFilterEntity), [UpdatedFilter]);

  const onApply = useCallback(() => updateFilter(cloneDeep(UpdatedFilter)), [UpdatedFilter, updateFilter]);
  const onCancel = useCallback(() => updateFilter(cloneDeep(InitFilterEntity)), [updateFilter]);

  const controls = useMemo(
    () => [
      {
        label: LABEL_CLEAR,
        onAction: onCancel,
        isAble: clearIsAble,
      },
      {
        label: LABEL_APPLY,
        onAction: onApply,
        isAble: applyIsAble,
      },
    ],
    [applyIsAble, clearIsAble, onApply, onCancel]
  );

  return (
    <StyledFilterContainerWrapper>
      <Button sx={EnhancedButtonStyle} endIcon={<FilterAltIcon />}>
        <Typography>Filter</Typography>
      </Button>
      <StyledFilterContainer>
        <StyledFilterOptions>
          <RadioGroupSelection {...countryProps} groupName="Country" totalEntities={TotalCountries} />
          <ModifiedAutocomplete {...provincesProps} groupName={ProvinceLabel} placeholder={ProvinceLabel.toLowerCase()} />
          <ModifiedSelect {...distanceProps} groupName={DistanceLabel} totalEntities={TotalDistance} labelFormatter={distanceLabelHandler} />
          {nameOfSearch === 'practitioner' && <TextFieldInput {...nearProps} groupName="Near" placeHolder="city or postal code" />}
          {nameOfSearch === 'practitioner' && <TextFieldInput {...nameProps} groupName="Name" placeHolder="practitioner name" />}
          {nameOfSearch !== 'practitioner' && <TextFieldInput {...nameProps} groupName="Name" placeHolder="facility name or facility type" />}
          {nameOfSearch === 'practitioner' && <RadioGroupSelection {...genderProps} groupName={GenderLabel} totalEntities={TotalGenders} />}
          {nameOfSearch === 'practitioner' && (
            <ModifiedAutocomplete
              {...languagesProps}
              groupName={LanguageMultiOptionsGroupName}
              placeholder={LanguageMultiOptionsGroupName.toLowerCase()}
            />
          )}
          {nameOfSearch === 'practitioner' && (
            <ModifiedAutocomplete {...catchmentAreaProps} groupName={CatchmentAreaGroupName} placeholder={CatchmentAreaGroupName.toLowerCase()} />
          )}
          {nameOfSearch === 'practitioner' && <ModifiedAutocomplete {...specialtyProps} groupName="Specialty" placeholder="Specialty" />}
        </StyledFilterOptions>
        <FilterControls controls={controls} />
      </StyledFilterContainer>
    </StyledFilterContainerWrapper>
  );
};

export default Filter;
